/**
 * 玩家回放 - 统一时间线 (Phase 2 核心端点)
 *
 * 渲染三类条目的混合时间线（events + ASR + 截图），按 occurred_at 升序。
 * 使用 AntD Timeline + 自定义 dot 图标（event=圆/utterance=三角/image=方）。
 * 支持游标分页（向上/向下翻页）、事件类型/分类过滤。
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Timeline,
  Card,
  Empty,
  Skeleton,
  Button,
  Space,
  Tag,
  Input,
  Select,
  message,
  Drawer,
  Typography,
} from 'antd'
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  AudioOutlined,
  PictureOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import { playbackApi } from '@/services/api'
import type {
  PlaybackTimelineItem,
  PlaybackTimelineResponse,
} from '@/types/playback'

const { Text } = Typography

type Tab = 'all' | 'event' | 'utterance' | 'image'

interface Props {
  worldId: string
  playerId: string
  /** 选中事件 id（来自子组件回调） */
  selectedEventId?: number | null
  /** 选中 ASR 序号 */
  selectedUtteranceSeq?: number | null
}

const PAGE_SIZE = 50

const TimelineView: React.FC<Props> = ({
  worldId,
  playerId,
  selectedEventId,
  selectedUtteranceSeq,
}) => {
  const [loading, setLoading] = useState(false)
  const [loadingDirection, setLoadingDirection] = useState<'after' | 'before' | null>(null)
  const [items, setItems] = useState<PlaybackTimelineItem[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<PlaybackTimelineResponse['next_cursor']>(null)
  // direction 不再放 state: 翻页方向由调用方在 fetch 时显式传入,
  // 避免 setDirection() 后闭包仍用旧值的竞态 (同事审查 STRUCT 3).
  // loadingDirection 只用于按钮 loading 态显示, 不参与 fetch 逻辑.

  // Filter
  const [tab, setTab] = useState<Tab>('all')
  const [filterEventType, setFilterEventType] = useState<string | undefined>(undefined)
  // filterEventCategory 由后端 /timeline 端点支持，用于按 event_category 精确过滤
  // 当前 UI 未暴露该过滤项，但保留 setter 以备后用
  const [filterEventCategory] = useState<string | undefined>(undefined)

  // Detail drawer
  const [detailItem, setDetailItem] = useState<PlaybackTimelineItem | null>(null)

  const fetchTimeline = useCallback(
    async (opts?: { reset?: boolean; direction?: 'after' | 'before' }) => {
      // 显式接收 direction, 不读 state, 避免闭包竞态.
      // opts.reset 强制 'after' (重置场景), 否则用 opts.direction, 兜底 'after'.
      const direction: 'after' | 'before' = opts?.reset
        ? 'after'
        : opts?.direction ?? 'after'
      setLoadingDirection(direction)
      setLoading(true)
      try {
        const params: Parameters<typeof playbackApi.getTimeline>[2] = {
          limit: PAGE_SIZE,
          direction,
        }
        if (!opts?.reset && nextCursor) {
          params.cursor_occurred_at = nextCursor.occurred_at
          params.cursor_id = nextCursor.id
        }
        if (tab !== 'all') {
          if (tab === 'event') {
            if (filterEventType) params.filter_event_type = filterEventType
            if (filterEventCategory) params.filter_event_category = filterEventCategory
          } else {
            // utterance/image 通过 client 端过滤
          }
        } else {
          if (filterEventType) params.filter_event_type = filterEventType
          if (filterEventCategory) params.filter_event_category = filterEventCategory
        }

        const response = await playbackApi.getTimeline(worldId, playerId, params)
        const newItems = response.data?.items ?? []

        if (opts?.reset) {
          setItems(newItems)
        } else if (direction === 'after') {
          setItems((prev) => [...prev, ...newItems])
        } else {
          setItems((prev) => [...newItems, ...prev])
        }

        setNextCursor(response.data?.next_cursor ?? null)
        setHasMore(response.data?.has_more ?? false)
      } catch (error: any) {
        message.error(error?.message ?? '加载时间线失败')
        console.error('fetchTimeline error:', error)
      } finally {
        setLoading(false)
        setLoadingDirection(null)
      }
    },
    [worldId, playerId, nextCursor, tab, filterEventType, filterEventCategory],
  )

  useEffect(() => {
    setItems([])
    setNextCursor(null)
    setHasMore(false)
    fetchTimeline({ reset: true })
  }, [tab, filterEventType, filterEventCategory, worldId, playerId])

  const filteredItems = useMemo(() => {
    if (tab === 'all') return items
    return items.filter((it) => it.kind === tab)
  }, [items, tab])

  // Auto-scroll to selected item
  useEffect(() => {
    if (selectedEventId != null || selectedUtteranceSeq != null) {
      const target = filteredItems.find((it) => {
        if (it.kind === 'event' && selectedEventId != null) return it.id === selectedEventId
        if (it.kind === 'utterance' && selectedUtteranceSeq != null)
          return it.utterance_seq === selectedUtteranceSeq
        return false
      })
      if (target) setDetailItem(target)
    }
  }, [selectedEventId, selectedUtteranceSeq, filteredItems])

  const handleLoadMore = () => {
    // 显式传 direction, 不依赖 state, 避免 setState 异步导致闭包读到旧值
    fetchTimeline({ direction: 'after' })
  }

  const handleLoadPrev = () => {
    // 显式传 direction, 不依赖 state, 避免 setState 异步导致闭包读到旧值
    fetchTimeline({ direction: 'before' })
  }

  const renderDot = (kind: PlaybackTimelineItem['kind']) => {
    if (kind === 'event') return <PlayCircleOutlined style={{ fontSize: 16, color: '#1890ff' }} />
    if (kind === 'utterance') return <AudioOutlined style={{ fontSize: 16, color: '#52c41a' }} />
    return <PictureOutlined style={{ fontSize: 16, color: '#722ed1' }} />
  }

  const renderItemContent = (item: PlaybackTimelineItem) => {
    if (item.kind === 'event') {
      return (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Space>
            <Tag color="blue">{item.event_category}</Tag>
            <Text strong>{item.event_type}</Text>
            {item.actor_type && <Tag>{item.actor_type}: {item.actor_id}</Tag>}
          </Space>
          {item.event_data && (
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {JSON.stringify(item.event_data).slice(0, 200)}
            </Text>
          )}
        </Space>
      )
    }
    if (item.kind === 'utterance') {
      return (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Space>
            <Tag color={item.speaker_role === 'player' ? 'blue' : item.speaker_role === 'actor' ? 'green' : 'default'}>
              {item.speaker_role}
            </Tag>
            {item.speaker_name && <Text>{item.speaker_name}</Text>}
            {item.utterance_seq != null && (
              <Text type="secondary" style={{ fontSize: 11 }}>#{item.utterance_seq}</Text>
            )}
          </Space>
          <Text>{item.text}</Text>
        </Space>
      )
    }
    // image
    return (
      <Space direction="vertical" size={4}>
        <Space>
          <Tag color="purple">截图</Tag>
          <Tag color={item.capture_status === 'uploaded' ? 'success' : 'warning'}>
            {item.capture_status}
          </Tag>
        </Space>
        {item.image_url ? (
          <a href={item.image_url} target="_blank" rel="noreferrer">
            <img
              src={item.image_url}
              alt="screenshot"
              style={{ maxWidth: 240, maxHeight: 160, borderRadius: 4 }}
            />
          </a>
        ) : (
          <Text type="secondary">无 URL</Text>
        )}
      </Space>
    )
  }

  const tabItems = [
    { key: 'all', label: `全部 ${items.length}` },
    { key: 'event', label: `事件 ${items.filter((i) => i.kind === 'event').length}` },
    { key: 'utterance', label: `ASR ${items.filter((i) => i.kind === 'utterance').length}` },
    { key: 'image', label: `截图 ${items.filter((i) => i.kind === 'image').length}` },
  ]

  return (
    <Card
      title={
        <Space>
          <span>统一时间线</span>
          <Tag>{filteredItems.length} 条</Tag>
        </Space>
      }
      size="small"
      extra={
        <Space>
          <Select
            value={tab}
            onChange={setTab}
            style={{ width: 120 }}
            options={tabItems.map((t) => ({ value: t.key, label: t.label }))}
          />
          <Input
            placeholder="事件类型过滤"
            allowClear
            value={filterEventType}
            onChange={(e) => setFilterEventType(e.target.value || undefined)}
            style={{ width: 160 }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => fetchTimeline({ reset: true })} loading={loading}>
            刷新
          </Button>
        </Space>
      }
    >
      {loading && items.length === 0 ? (
        <Skeleton active />
      ) : filteredItems.length === 0 ? (
        <Empty description="暂无时间线数据" />
      ) : (
        <>
          <Space className="mb-4">
            <Button
              icon={<ArrowDownOutlined />}
              onClick={handleLoadPrev}
              loading={loading && loadingDirection === 'before'}
              disabled={!items.length}
            >
              加载更早
            </Button>
            <Button
              type="primary"
              icon={<ArrowUpOutlined />}
              onClick={handleLoadMore}
              loading={loading && loadingDirection === 'after'}
              disabled={!hasMore}
            >
              {hasMore ? '加载更多' : '已到末尾'}
            </Button>
          </Space>

          <Timeline
            mode="left"
            items={filteredItems.map((item) => ({
              dot: renderDot(item.kind),
              color:
                item.kind === 'event'
                  ? 'blue'
                  : item.kind === 'utterance'
                  ? 'green'
                  : 'purple',
              children: (
                <div
                  className="cursor-pointer hover:bg-gray-50 p-2 rounded"
                  onClick={() => setDetailItem(item)}
                >
                  <Space style={{ marginBottom: 4 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(item.occurred_at).format('HH:mm:ss.SSS')}
                    </Text>
                  </Space>
                  {renderItemContent(item)}
                </div>
              ),
            }))}
          />
        </>
      )}

      <Drawer
        title={`详情 (${detailItem?.kind ?? ''})`}
        open={!!detailItem}
        onClose={() => setDetailItem(null)}
        width={640}
      >
        {detailItem && (
          <pre className="text-xs whitespace-pre-wrap break-all">
            {JSON.stringify(detailItem, null, 2)}
          </pre>
        )}
      </Drawer>
    </Card>
  )
}

export default TimelineView